import i18n from 'i18next';

import type { LanguageCode } from '@/stores/locale';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const AVG_DAYS_PER_MONTH = 30.4375;

const normalizeTimestamp = (timestamp: number): number =>
  timestamp < 10000000000 ? timestamp * 1000 : timestamp;

/**
 * Formats how long an employee has been employed as a localized
 * "X yrs, Y months" string. Returns an empty string for invalid input.
 */
export const formatEmploymentDuration = (
  employedSince: Date | number | string | null | undefined,
  locale: LanguageCode,
): string => {
  if (employedSince == null) return '';

  let start: Date;
  if (typeof employedSince === 'number') {
    start = new Date(normalizeTimestamp(employedSince));
  } else if (employedSince instanceof Date) {
    start = employedSince;
  } else {
    start = new Date(employedSince);
  }

  if (Number.isNaN(start.getTime())) return '';

  const diffMs = Date.now() - start.getTime();
  if (diffMs <= 0) return '';

  const totalMonths = Math.floor(diffMs / MS_PER_DAY / AVG_DAYS_PER_MONTH);
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  const yrs = i18n.t('details.YearsShort', { ns: 'employees', lng: locale });
  const mos = i18n.t('details.MonthsShort', { ns: 'employees', lng: locale });

  if (years === 0) return `${months} ${mos}`;
  if (months === 0) return `${years} ${yrs}`;
  return `${years} ${yrs}, ${months} ${mos}`;
};
